import { ReactElement } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { formatDate } from "~/utils";
import DisplayAbove from "~/components/styled/DisplayAbove";
import dynamic from "next/dynamic";
import { Heading } from "~/components/ui/Heading";
import { ContentWithSidebar } from "~/components/styled/ContentWithSidebar";
import { FlexibleContent } from "~/components/flexibleContent/FlexibleContent";
import StickySidebar from "~/components/sidebars/StickySidebar";
import SubMenuTwoCol from "~/components/styled/SubMenuTwoCol";
import { SidebarShare } from "~/components/sidebars/SidebarShare";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { ApiImage } from "~/components/ui/ApiImage";
import HeroSingleImage from "~/components/content/HeroSingleImage";
import { themeImgSizes } from "~/theme";
import { appConfig } from "~/config";

const HeroSlideShow = dynamic(
  () => import("~/components/content/HeroSlideShow")
);

import { HeroSlideshowContainer } from "~/components/styled/HeroSlideshowContainer";
import { heroSlideshowImgSizes } from "~/theme/imgSizes";
import { RelatedContent } from "~/components/content/RelatedContent";
import { RelatedProducts } from "~/components/content/RelatedProducts";
import { InstagramPosts } from "~/components/content/InstagramPosts";

const NewsDetail = ({ data }: { data: any }) => {
  const config = useConfigContext();

  let type = data?.acf?.type?.trim()
    ? data?.acf?.type?.trim()
    : data?.taxonomies?.category?.[0]?.name;

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={
          data?.yoast_head_json?.title ??
          `${data?.title} - News - ${appConfig.appTitle}`
        }
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header>
        <SubMenuTwoCol width="auto" alignSelf="center" colGapSpace={8}>
          <Heading space={7} heading="h3" inline trimmed html={data?.title} />

          <DisplayAbove breakpoint="tabletLandscape" inline>
            <Heading space={0} heading="h4" inline>
              {type ?? "News"}&emsp;
              <span style={{ fontWeight: 400 }}>{formatDate(data?.date)}</span>
            </Heading>
          </DisplayAbove>
        </SubMenuTwoCol>
      </Header>
      <main id="content">
        {data?.acf?.hero_images?.length > 0 ||
        data?.featuredImage?.sizes?.level_1 ? (
          data.acf.hero_images?.length > 1 ? (
            <HeroSlideshowContainer>
              <HeroSlideShow
                hasYPadding
                slides={data.acf.hero_images.map((img: any, index: number) => (
                  <ApiImage
                    key={`slideshowimg-${index}`}
                    aspectRatioDirection="height"
                    alt={img?.alt}
                    sizes={img?.sizes}
                    imgSizes={themeImgSizes(heroSlideshowImgSizes)}
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                ))}
              />
            </HeroSlideshowContainer>
          ) : (
            <HeroSingleImage>
              <ApiImage
                bgColor="#fff"
                key={`slideshowimg`}
                aspectRatioDirection="width"
                alt={
                  data?.acf?.hero_images?.length > 0
                    ? data.acf.hero_images?.[0]?.alt
                    : ""
                }
                sizes={
                  data?.acf?.hero_images?.length > 0
                    ? data.acf.hero_images?.[0]?.sizes
                    : data?.featuredImage?.sizes
                }
                imgSizes={themeImgSizes(heroSlideshowImgSizes)}
                mode="fill"
                objectFit="contain"
                objectPosition="left top"
                loading="eager"
              />
            </HeroSingleImage>
          )
        ) : (
          <></>
        )}

        <PageMargins
          spaceTop={!data?.acf?.hero_images?.length ? 7 : undefined}
          spaceBottom={5}
        >
          <Heading space={7} heading="h4">
            {type ?? "News"}&emsp;
            <span style={{ fontWeight: 400 }}>{formatDate(data?.date)}</span>
          </Heading>

          <Heading
            asTag
            heading="h1"
            space={data?.acf?.sub_title.trim() ? 8 : 6}
            html={data?.title}
          />

          {data?.acf?.sub_title.trim() && (
            <Heading heading="h2" space={6} html={data?.acf?.sub_title} />
          )}

          <ContentWithSidebar>
            {data?.acf?.content?.length > 0 ? (
              <FlexibleContent
                columnWidth="full"
                field="content"
                content={data.acf.content}
              />
            ) : (
              <div />
            )}

            <StickySidebar>
              <SidebarShare
                title={data?.title}
                url={`${config.baseUrl}/news/view/${data?.slug}`}
              />
            </StickySidebar>
          </ContentWithSidebar>
          {/* xxx/TODO show all posts that are in the same category  */}
        </PageMargins>
        <RelatedProducts
          key={`news-products-${data?.id}`}
          id={data?.id}
          ids={data?.acf?.shop_items ?? []}
        />
        <RelatedContent
          key={`page-related-${data?.id}`}
          ids={[data?.id]}
          relatedItems={data?.acf?.related_content ?? []}
        />
        <InstagramPosts />
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const events = await restApiESQuery({
    type: "post",
    perPage: 100,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: events?.data?.length
      ? events?.data.map((item: any) => item.url)
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const data = await restApiGetPostBySlugOrFallbackId(
    "post",
    context?.params?.slug,
    token
  );

  const frontendSettings = await restApiESGetSettings(["category"]);
  if (!data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      data,
    },
    revalidate: frontendSettings?.options?.copyrightNotice ?  appConfig.revalidateInterval("newsDetail", {
      date: data?.date,
      modified: data?.modified,
    }) : 15,
  };
};

NewsDetail.getLayout = function getLayout(page: ReactElement, props: any) {
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default NewsDetail;
